/**
 * App-wide switches (GET /api/settings), shared by every screen.
 *
 * Read once and cached, and re-read whenever a screen that depends on a switch
 * opens (useAppSettings({ refresh: true })), so a technician sees the main admin's
 * change the next time they open the camera, without reloading the app.
 */
import { useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

// Until the server answers, every feature reads as off, so nothing appears and then vanishes.
let current = { videoEnabled: false, nearbyJobsEnabled: false, changes: {}, loaded: false };
const listeners = new Set();
let inFlight = null;

function set(next) {
  current = { ...current, ...next, loaded: true };
  listeners.forEach((fn) => fn(current));
}

export function refreshAppSettings() {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(current);
  if (!inFlight) {
    inFlight = fetch(apiUrl('/api/settings'), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) set(data); return current; })
      .catch(() => current) // offline: keep the last known values
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/**
 * Main admin only: the server refuses anyone else. feature is 'video' or
 * 'nearby-jobs'. Returns the new settings.
 */
export async function setFeature(feature, enabled) {
  const res = await fetch(apiUrl(`/api/settings/${feature}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify({ enabled }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.message || 'Could not change the setting');
  set(data);
  return current;
}

export function useAppSettings({ refresh = false } = {}) {
  const [value, setValue] = useState(current);
  useEffect(() => {
    listeners.add(setValue);
    if (refresh || !current.loaded) refreshAppSettings();
    return () => listeners.delete(setValue);
  }, [refresh]);
  return value;
}
